import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import axios from 'axios';
import L from 'leaflet';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [35.3, 75.6]; 
const DEFAULT_ZOOM = 6;

const RecenterMap = ({ center, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (center) map.setView(center, zoom ?? map.getZoom());
  }, [center, zoom, map]);
  return null;
};

const ClickHandler = ({ onPick }) => {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const LocationPicker = ({
  latitude,
  longitude,
  onLocationChange,
  onReverseGeocode,
  searchQuery,
  height = 300,
}) => {
  const [position, setPosition] = useState(
    latitude && longitude ? [parseFloat(latitude), parseFloat(longitude)] : null
  );
  const [mapCenter, setMapCenter] = useState(position || DEFAULT_CENTER);
  const [searching, setSearching] = useState(false);
  const [reverseLoading, setReverseLoading] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (latitude && longitude) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      if (!isNaN(lat) && !isNaN(lng)) {
        setPosition([lat, lng]);
      }
    }
  }, [latitude, longitude]);

  useEffect(() => {
    if (!searchQuery || searchQuery.trim().length < 3) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(() => {
      setSearching(true);

      axios
        .get('https://nominatim.openstreetmap.org/search', {
          params: {
            q: `${searchQuery}, Pakistan`,
            format: 'json',
            limit: 1,
            addressdetails: 1,
          },
        })
        .then((res) => {
          if (res.data && res.data.length > 0) {
            const result = res.data[0];
            const lat = parseFloat(result.lat);
            const lng = parseFloat(result.lon);

            setPosition([lat, lng]);
            setMapCenter([lat, lng]);
            onLocationChange(lat, lng);

            runReverseGeocode(lat, lng);
          }
        })
        .catch((err) => {
          console.log('Forward geocode error:', err);
        })
        .finally(() => {
          setSearching(false);
        });
    }, 800);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const runReverseGeocode = (lat, lng) => {
    if (!onReverseGeocode) return;

    setReverseLoading(true);

    axios
      .get('https://nominatim.openstreetmap.org/reverse', {
        params: {
          lat,
          lon: lng,
          format: 'json',
          addressdetails: 1,
        },
      })
      .then((res) => {
        const addr = res.data?.address || {};

        const province =
          addr.state || addr.province || addr.region || '';
        const district =
          addr.county || addr.state_district || addr.city_district || '';
        const nearest_city =
          addr.city || addr.town || addr.village || addr.suburb || '';

        onReverseGeocode({ province, district, nearest_city });
      })
      .catch((err) => {
        console.log('Reverse geocode error:', err);
      })
      .finally(() => {
        setReverseLoading(false);
      });
  };

  const handlePick = (lat, lng) => {
    setPosition([lat, lng]);
    onLocationChange(lat, lng);
    runReverseGeocode(lat, lng);
  };

  return (
    <div className="adm-location-picker">
      {(searching || reverseLoading) && (
        <div className="adm-location-picker-status">
          {searching ? 'Searching location...' : 'Detecting district/province...'}
        </div>
      )}

      <div style={{ height, borderRadius: 8, overflow: 'hidden' }}>
        <MapContainer
          center={mapCenter}
          zoom={DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterMap center={mapCenter} />
          <ClickHandler onPick={handlePick} />

          {position && (
            <Marker
              position={position}
              draggable
              eventHandlers={{
                dragend: (e) => {
                  const { lat, lng } = e.target.getLatLng();
                  handlePick(lat, lng);
                },
              }}
            />
          )}
        </MapContainer>
      </div>

      <small className="text-muted d-block mt-1">
        Click on the map or drag the marker to set the exact location.
      </small>
    </div>
  );
};

export default LocationPicker;